import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import Button from "../../../component-lib/Molecules/button/Button";

// IMPORT RESULTS DIALOG
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    borderRadius: "8px",
    height: "auto",
    width: "900px",
    maxWidth: "none",
  },
}));

interface TemplateImportResultsDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  results: {
    summary: {
      totalRecords: number;
      validRecords: number;
      invalidRecords: number;
      createdTemplates: number;
    };
    valid: Array<Record<string, unknown>>;
    invalid: Array<{
      row: number;
      data: Record<string, unknown>;
      errors: string[];
    }>;
    message: string;
  } | null;
}

export default function TemplateImportResultsDialog({
  isOpen = false,
  handleClose,
  results,
}: Readonly<TemplateImportResultsDialogProps>) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Reset pagination when dialog opens or results change
  React.useEffect(() => {
    if (isOpen && results) {
      setCurrentPage(1);
      setRowsPerPage(10);
    }
  }, [isOpen, results]);

  if (!results) {
    return null;
  }

  const { summary, invalid } = results;

  // Calculate pagination
  const totalPages = Math.ceil(invalid.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedInvalid = invalid.slice(startIndex, endIndex);

  // Summary cards data
  const summaryCards = [
    {
      title: "Total Records",
      value: summary.totalRecords.toString(),
      color: "#1976d2",
      bgColor: "#e3f2fd",
    },
    {
      title: "Valid Records",
      value: summary.validRecords.toString(),
      color: "#2e7d32",
      bgColor: "#e8f5e8",
    },
    {
      title: "Invalid Records",
      value: summary.invalidRecords.toString(),
      color: "#d32f2f",
      bgColor: "#ffebee",
    },
  ];

  // Table columns for invalid records
  const invalidRecordsColumns = [
    {
      field: "row",
      label: <div>Row No.</div>,
      renderLogic: (row: {
        row: number;
        data: Record<string, unknown>;
        errors: string[];
      }) => {
        return <p className="text-neutral-90 font-normal">{row.row}</p>;
      },
    },
    {
      field: "name",
      label: <div>Template Name</div>,
      renderLogic: (row: {
        row: number;
        data: Record<string, unknown>;
        errors: string[];
      }) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row.data.name ? String(row.data.name) : "N/A"}
          </p>
        );
      },
    },
    {
      field: "description",
      label: <div>Description</div>,
      renderLogic: (row: {
        row: number;
        data: Record<string, unknown>;
        errors: string[];
      }) => {
        const description = row.data.description
          ? String(row.data.description)
          : "N/A";
        return (
          <Tooltip title={description} arrow placement="top">
            <p className="text-neutral-90 font-normal" style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {description}
            </p>
          </Tooltip>
        );
      },
    },
    {
      field: "errors",
      label: <div>Error Reason</div>,
      renderLogic: (row: {
        row: number;
        data: Record<string, unknown>;
        errors: string[];
      }) => {
        const errorMessage = row.errors?.join(", ") || "Unknown error";
        return (
          <Tooltip title={errorMessage} arrow placement="top">
            <Chip
              label={errorMessage}
              size="small"
              style={{
                backgroundColor: "#ffebee",
                color: "#d32f2f",
                fontSize: "12px",
                maxWidth: "300px",
                height: "auto",
                whiteSpace: "normal",
                wordBreak: "break-word",
                cursor: "default",
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="template-import-results-dialog-title"
      open={isOpen}
    >
      <div
        className="flex justify-between items-center rounded-lg"
        style={{ margin: "14px 24px" }}
      >
        <Heading
          title={
            results.summary.invalidRecords > 0
              ? "Import Completed with Issues"
              : "Import Completed Successfully"
          }
        />
        <button
          onClick={handleClose}
          className="cursor-pointer border-none bg-transparent p-0"
          type="button"
          aria-label="Close dialog"
        >
          <CloseIcon />
        </button>
      </div>

      <DialogContent
        dividers
        style={{ padding: "16px 24px 24px 24px", borderRadius: "8px" }}
      >
        <div className="flex flex-col gap-6">
          {/* Summary Section */}
          <div className="flex gap-4 justify-center">
            {summaryCards.map((card) => (
              <Box
                key={card.title}
                sx={{
                  backgroundColor: card.bgColor,
                  color: card.color,
                  padding: "12px 20px",
                  borderRadius: "8px",
                  textAlign: "center",
                  minWidth: "120px",
                }}
              >
                <Typography
                  variant="h6"
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    marginBottom: "4px",
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  {card.title}
                </Typography>
              </Box>
            ))}
          </div>

          {/* Message */}
          <div className="text-center">
            <Typography
              variant="body1"
              style={{
                color: summary.invalidRecords > 0 ? "#d32f2f" : "#2e7d32",
                fontWeight: "500",
              }}
            >
              {results.message}
            </Typography>
          </div>

          {/* Invalid Records Table */}
          {invalid.length > 0 && (
            <div>
              <Typography
                variant="h6"
                style={{
                  marginBottom: "16px",
                  fontWeight: "600",
                  color: "#d32f2f",
                }}
              >
                Invalid Records Details
              </Typography>
              <Table
                columns={invalidRecordsColumns}
                rows={paginatedInvalid}
                paginationProps={{
                  isPagination: true,
                  page: currentPage,
                  totalPages: totalPages,
                  totalCount: invalid.length,
                  limit: rowsPerPage.toString(),
                  rowsPerPageOptions: [10, 25, 50],
                  onPageChange: (page: number) => {
                    setCurrentPage(page);
                  },
                  handleChangeRowsPerPage: (newRowsPerPage: string) => {
                    setRowsPerPage(parseInt(newRowsPerPage));
                    setCurrentPage(1); // Reset to first page when changing rows per page
                  },
                }}
              />
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end mt-4">
            <Button
              label="Close"
              variant="contained"
              onClickCb={handleClose}
              style={{
                width: "120px",
                height: "44px",
                padding: "12px 20px",
                fontSize: "16px",
                backgroundColor: "#005596",
                color: "#FFFFFF",
                border: "1px solid #005596",
                fontFamily: "Inter",
              }}
            />
          </div>
        </div>
      </DialogContent>
    </BootstrapDialog>
  );
}

